/**
 * Card "Fuso horário" (Branding/Configurações): o fuso IANA do restaurante,
 * base das janelas de promoção e de prato do dia (#200). A API valida o nome
 * contra o tzdata do runtime; aqui a lista vem do próprio browser
 * (Intl.supportedValuesOf), com prévia da hora local para o dono conferir.
 */
import { useMemo, useState } from 'react'
import { useMutation, type useQueryClient } from '@tanstack/react-query'
import { config, type RestaurantConfig } from '../lib/api.js'
import { useI18n } from '../lib/i18n.js'
import { Card, Button, Select, toast } from './ui/index.js'

// browsers sem Intl.supportedValuesOf (Safari < 15.4): fusos dos mercados atuais
const FALLBACK_ZONES = [
  'Europe/Lisbon',
  'Atlantic/Azores',
  'Atlantic/Madeira',
  'Europe/Madrid',
  'Atlantic/Canary',
  'America/Sao_Paulo',
  'America/Manaus',
  'America/New_York',
  'UTC',
]

function availableZones(current: string): string[] {
  let zones: string[]
  try {
    zones = Intl.supportedValuesOf('timeZone')
  } catch {
    zones = FALLBACK_ZONES
  }
  return zones.includes(current) ? zones : [current, ...zones]
}

function offsetLabel(tz: string): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')
    return part ? part.value.replace('GMT', 'UTC') : ''
  } catch {
    return ''
  }
}

function localTimePreview(tz: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date())
  } catch {
    return ''
  }
}

export function TimezoneForm({
  cfg,
  qc,
}: {
  cfg: RestaurantConfig
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t } = useI18n()
  const [tz, setTz] = useState(cfg.timezone)

  const options = useMemo(
    () =>
      availableZones(cfg.timezone).map((z) => {
        const off = offsetLabel(z)
        return { value: z, label: off ? `${z.replace(/_/g, ' ')} (${off})` : z }
      }),
    [cfg.timezone],
  )

  const mut = useMutation({
    mutationFn: (timezone: string) => config.update({ timezone }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['config'] })
      toast.success(t('admin.branding.tz_saved'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Card title={t('admin.branding.tz_title')} description={t('admin.branding.tz_desc')}>
      <div className="space-y-4" data-testid="timezone-form">
        <Select
          label={t('admin.branding.tz_label')}
          value={tz}
          onChange={(e) => setTz(e.target.value)}
          options={options}
          data-testid="timezone-select"
        />
        <p className="text-sm text-gray-500">
          {t('admin.branding.tz_now')}{' '}
          <span className="font-medium text-gray-700">
            {localTimePreview(tz, cfg.primary_locale)}
          </span>
        </p>
        <Button
          loading={mut.isPending}
          disabled={tz === cfg.timezone}
          onClick={() => mut.mutate(tz)}
        >
          {t('admin.branding.tz_save')}
        </Button>
      </div>
    </Card>
  )
}
